// src/components/itinerary/TripSelector.tsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Chip,
  Button,
  Skeleton,
  Collapse
} from '@mui/material';
import { format } from 'date-fns';
import { Trip } from '../../contexts/ItineraryContext';

interface TripSelectorProps {
  trips: Trip[];
  selectedTrip: Trip | null;
  loading: boolean;
  onSelectTrip: (trip: Trip | null) => void;
}

const TripSelector: React.FC<TripSelectorProps> = ({
  trips,
  selectedTrip,
  loading,
  onSelectTrip
}) => {
  if (loading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Select a Trip
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={30} />
                  <Skeleton variant="text" width="80%" />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Skeleton variant="rectangular" width={60} height={24} />
                    <Skeleton variant="rectangular" width={60} height={24} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (trips.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Trips Found
        </Typography>
        <Typography variant="body1" paragraph>
          You need to create a trip before you can build an itinerary.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component="a"
          href="/trip-planner"
        >
          Create a Trip
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ mb: 2, overflow: 'hidden' }}>
      <Typography variant="h5" gutterBottom>
        {selectedTrip ? 'Selected Trip' : 'Select a Trip'}
      </Typography>
      
      {/* Show all trips when no trip is selected, otherwise just show the selected trip */}
      <Collapse in={!selectedTrip} timeout={300}>
        <Grid container spacing={2} sx={{ display: selectedTrip ? 'none' : 'flex' }}>
          {trips.map((trip) => (
            <Grid item xs={12} sm={6} md={4} key={trip.id}>
              <Card 
                variant="outlined"
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
                onClick={() => onSelectTrip(trip)}
              >
                <CardContent>
                  <Typography variant="h6" noWrap>
                    {trip.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {trip.destination} • {format(trip.startDate, 'MMM d')} - {format(trip.endDate, 'MMM d, yyyy')}
                  </Typography>
                  
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {trip.purpose && (
                      <Chip label={trip.purpose} size="small" color="primary" variant="outlined" />
                    )}
                    {trip.groupSize && (
                      <Chip 
                        label={`${trip.groupSize} ${trip.groupSize === 1 ? 'Person' : 'People'}`} 
                        size="small" 
                        color="default" 
                        variant="outlined" 
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Collapse>
      
      {/* Selected Trip Display */}
      {selectedTrip && (
        <Collapse in={!!selectedTrip} timeout={300}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Card 
              variant="outlined"
              sx={{ 
                borderColor: 'primary.main',
                boxShadow: 1,
                width: '100%',
                maxWidth: 500
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Typography variant="h6" noWrap>
                  {selectedTrip.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedTrip.destination} • {format(selectedTrip.startDate, 'MMM d')} - {format(selectedTrip.endDate, 'MMM d, yyyy')}
                </Typography>
                
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedTrip.purpose && (
                    <Chip label={selectedTrip.purpose} size="small" color="primary" variant="outlined" />
                  )}
                  {selectedTrip.groupSize && (
                    <Chip 
                      label={`${selectedTrip.groupSize} ${selectedTrip.groupSize === 1 ? 'Person' : 'People'}`} 
                      size="small" 
                      color="default" 
                      variant="outlined" 
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
            
            <Button 
              variant="text" 
              color="primary"
              onClick={() => onSelectTrip(null)}
              sx={{ ml: 2 }}
            >
              Change Trip
            </Button>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default TripSelector;